import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { updateThreshold, type AnomalyThreshold } from "@/services/securityRecords";

interface Props {
  threshold: AnomalyThreshold;
  onClose: () => void;
  onSaved: () => void;
}

function getErrorText(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    if (status === 401 || status === 403) {
      return "权限不足，无法保存。请联系管理员。";
    }
    if (status === 404) {
      return "配置数据未找到，请刷新页面后重试。";
    }
    if (err.code === "ECONNABORTED" || !err.response) {
      return "网络连接超时，请稍后重试。";
    }
  }
  return "保存失败，请稍后重试。";
}

export default function ThresholdEditModal({ threshold, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [timeWindow, setTimeWindow] = useState(threshold.timeWindowMinutes);
  const [countThreshold, setCountThreshold] = useState(threshold.countThreshold);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setTimeWindow(threshold.timeWindowMinutes);
    setCountThreshold(threshold.countThreshold);
    setError("");
  }, [threshold]);

  const titleMap: Record<string, string> = {
    frequency: t("anomaly.threshold.frequencyTitle") || "频率异常",
    duplicate: t("anomaly.threshold.duplicateTitle") || "重复问题",
  };

  const modalTitle = (t("anomaly.modal.title") || "编辑阈值").replace("{name}", titleMap[threshold.metricType] || "");

  const timeWindowLabel = t("anomaly.modal.timeWindow") || "时间窗口";
  const countLabel = t("anomaly.modal.countThreshold") || "次数上限";
  const cancelText = t("common.cancel") || "取消";
  const saveText = t("common.save") || "保存";
  const loadingText = t("common.loading") || "加载中...";
  const invalidValueText = t("anomaly.modal.invalidValue") || "时间窗口和次数上限必须大于 0";
  const minutesText = t("anomaly.threshold.minutes") || "分钟";
  const timesText = t("anomaly.threshold.times") || "次";

  const handleSave = async () => {
    if (timeWindow <= 0 || countThreshold <= 0) {
      setError(invalidValueText);
      return;
    }

    setSaving(true);
    setError("");

    try {
      await updateThreshold(threshold.metricType, {
        timeWindowMinutes: timeWindow,
        countThreshold: countThreshold,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(getErrorText(err));
    } finally {
      setSaving(false);
    }
  };

  const descText =
    threshold.metricType === "duplicate"
      ? t("anomaly.modal.desc.duplicate", { minutes: timeWindow, count: countThreshold })
      : t("anomaly.modal.desc.frequency", { minutes: timeWindow, count: countThreshold });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">
            {modalTitle}
          </h3>
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              {timeWindowLabel}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={120}
                value={timeWindow}
                onChange={(e) => setTimeWindow(Number(e.target.value))}
                className="w-20 px-2.5 py-1.5 border border-gray-200 rounded-md text-sm text-gray-900 focus:outline-none focus:border-gray-400"
              />
              <span className="text-xs text-gray-400">{minutesText}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">
              {countLabel}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={1000}
                value={countThreshold}
                onChange={(e) => setCountThreshold(Number(e.target.value))}
                className="w-20 px-2.5 py-1.5 border border-gray-200 rounded-md text-sm text-gray-900 focus:outline-none focus:border-gray-400"
              />
              <span className="text-xs text-gray-400">{timesText}</span>
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 rounded-md px-2.5 py-1.5">{error}</p>
          )}

          <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2.5">
            <p className="text-xs text-gray-500">{descText}</p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            {cancelText}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 text-xs text-white bg-[#830051] rounded-md hover:bg-[#6a0042] transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
          >
            {saving ? loadingText : saveText}
          </button>
        </div>
      </div>
    </div>
  );
}